import { useContext, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AuthContext } from 'State/AuthContext';
import { AuthFunctionsProvider } from 'State/AuthFunctionsContext';
import SB_AUTH_PAGE from 'Pages/SbAuthPage/SbAuthPage';
import SessionChecker from 'Components/SbSessionCheckerComponent/SbSessionChecker';

/**
 * AuthGuard
 *
 * A pathless layout-route component that gates every V2 app route.
 * Used as the outermost wrapper in ProtectedRoutesV2:
 *
 *   <Route element={<AuthGuard />}>
 *     <Route element={<LayoutV2 />}>
 *       ...page routes
 *     </Route>
 *   </Route>
 *
 * Responsibilities (in priority order):
 *
 *  1. SSO redirect
 *     If the user authenticated via SSO but the token is missing (e.g. hard
 *     refresh before the callback completes), send them back to the IdP.
 *
 *  2. Global loading state
 *     `initializing` stays true until entitlements + orgMetaData + error
 *     messages have all loaded. A centered spinner is shown during this time.
 *
 *  3. Unauthenticated
 *     No access token → render the login page wrapped in AuthFunctionsProvider
 *     (provides login/SSO callbacks to the auth page).
 *
 *  4. Authenticated
 *     Render <SessionChecker /> (handles token refresh / timeout warnings)
 *     followed by <Outlet /> which renders the rest of the V2 route tree
 *     (LayoutV2 → page component).
 */
const AuthGuard = () => {
  const { accessToken, initializing } = useContext(AuthContext);

  // ── SSO redirect ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (
      localStorage.getItem('auth_type') === 'sso' &&
      !localStorage.getItem('access_token')
    ) {
      window.location.href = localStorage.getItem('sso_idp_login_url') || '/';
    }
  }, []);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (initializing) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: '100vh' }}
      >
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  // ── Unauthenticated ───────────────────────────────────────────────────────
  if (!accessToken) {
    return (
      <div className="home-page">
        <AuthFunctionsProvider>
          <SB_AUTH_PAGE />
        </AuthFunctionsProvider>
      </div>
    );
  }

  // ── Authenticated ─────────────────────────────────────────────────────────
  return (
    <>
      <SessionChecker />
      <Outlet />
    </>
  );
};

export default AuthGuard;
